import { ComponentCategory, ComponentProp, DesignSystemComponent } from '../types';
import { parseComponentAST, extractClassCode } from 'ast/componentParser';

// Generate display-friendly name from component tag name
function generateDisplayName(tagName: string): string {
	// Remove ds- prefix and split by hyphens
	const withoutPrefix = tagName.replace(/^ds-/, '');
	const words = withoutPrefix.split('-').map((word) => {
		return word.charAt(0).toUpperCase() + word.slice(1);
	});
	return words.join(' ');
}

// Infer component category from tag name or description
function inferCategory(tagName: string, description?: string): ComponentCategory {
	const lowerTag = tagName.toLowerCase();
	const lowerDesc = (description || '').toLowerCase();

	if (lowerTag.includes('button') || lowerDesc.includes('button')) {
		return 'button';
	}
	if (lowerTag.includes('card') || lowerDesc.includes('card')) {
		return 'card';
	}
	if (
		lowerTag.includes('input') ||
		lowerTag.includes('field') ||
		lowerTag.includes('form') ||
		lowerDesc.includes('input') ||
		lowerDesc.includes('field') ||
		lowerDesc.includes('form')
	) {
		return 'form';
	}
	if (
		lowerTag.includes('sidebar') ||
		lowerTag.includes('nav') ||
		lowerTag.includes('menu') ||
		lowerTag.includes('footer') ||
		lowerTag.includes('breadcrumb') ||
		lowerTag.includes('tab') ||
		lowerDesc.includes('navigation')
	) {
		return 'navigation';
	}
	if (
		lowerTag.includes('table') ||
		lowerTag.includes('stat') ||
		lowerTag.includes('badge') ||
		lowerTag.includes('avatar') ||
		lowerDesc.includes('data')
	) {
		return 'data-display';
	}

	return 'other';
}

// Extract props from component class code
function extractProps(classCode: string): ComponentProp[] {
	const props: ComponentProp[] = [];
	const propNames = new Set<string>();

	// Find all getAttribute calls
	const getAttributeRegex = /this\.getAttribute\(['"]([^'"]+)['"]\)/g;
	let match;

	while ((match = getAttributeRegex.exec(classCode)) !== null) {
		const propName = match[1];
		if (!propNames.has(propName)) {
			propNames.add(propName);
		}
	}

	// For each prop, analyze its usage to determine type and default
	for (const propName of propNames) {
		const prop: ComponentProp = {
			name: propName,
			type: 'string', // Default to string
		};

		// Find the line(s) where this prop is used
		const propRegex = new RegExp(`this\\.getAttribute\\(['"]${propName}['"]\\)[^;]*`, 'g');
		const propMatch = propRegex.exec(classCode);

		if (propMatch) {
			const propUsage = propMatch[0];

			// Check for default value (|| 'default')
			const defaultMatch = propUsage.match(/\|\|\s*['"]([^'"]+)['"]/);
			if (defaultMatch) {
				prop.defaultValue = defaultMatch[1];
			}

			// Check for JSON.parse
			if (propUsage.includes('JSON.parse')) {
				prop.type = 'json';
				// Try to find default JSON value
				const jsonDefaultMatch = propUsage.match(/\|\|\s*(\[.*?\]|\{.*?\})/s);
				if (jsonDefaultMatch) {
					try {
						prop.defaultValue = JSON.stringify(JSON.parse(jsonDefaultMatch[1]));
					} catch {
						// Invalid JSON, skip
					}
				}
			}

			// Check for number usage
			if (
				propUsage.match(/parseInt|parseFloat|\+\s*this\.getAttribute|Number\(/) ||
				propUsage.match(/\d+\s*[\+\-\*\/]/)
			) {
				prop.type = 'number';
			}

			// Check for boolean usage
			if (
				propUsage.includes('hasAttribute') ||
				propUsage.match(/===?\s*(true|false)|!==?\s*(true|false)/)
			) {
				prop.type = 'boolean';
			}

			// Check for enum/select pattern (ternary with multiple options)
			const ternaryMatches = propUsage.match(/(['"]\w+['"])\s*\?/g);
			if (ternaryMatches && ternaryMatches.length > 1) {
				// Extract all possible values
				const values = new Set<string>();
				ternaryMatches.forEach((m) => {
					const valueMatch = m.match(/['"]([^'"]+)['"]/);
					if (valueMatch) {
						values.add(valueMatch[1]);
					}
				});
				if (defaultMatch) {
					values.add(defaultMatch[1]);
				}
				if (values.size > 1) {
					prop.type = 'select';
					prop.options = Array.from(values);
				}
			}

			// Check for specific enum patterns in the class
			const enumPattern = new RegExp(`${propName}\\s*===?\\s*['"]([^'"]+)['"]`, 'g');
			const enumValues = new Set<string>();
			let enumMatch;
			while ((enumMatch = enumPattern.exec(classCode)) !== null) {
				enumValues.add(enumMatch[1]);
			}
			if (enumValues.size > 1 && prop.type === 'string') {
				prop.type = 'select';
				prop.options = Array.from(enumValues);
				if (defaultMatch) {
					prop.options.push(defaultMatch[1]);
				}
			}
		}

		props.push(prop);
	}

	return props;
}

// Extract description from comment before component
function extractDescription(lines: string[], componentStartIndex: number): string | undefined {
	let description: string | undefined;
	let i = componentStartIndex - 1;

	// Look backwards for comments (up to 5 lines)
	while (i >= 0 && i >= componentStartIndex - 5) {
		const line = lines[i].trim();
		if (line.startsWith('/*') && line.endsWith('*/')) {
			const commentText = line.replace(/\/\*\s*|\s*\*\//g, '').trim();
			// Skip section comments (they have dashes or are empty)
			if (!commentText.includes('---') && commentText.length > 0) {
				description = commentText;
				break;
			}
		}
		i--;
	}

	return description;
}

// Generate example HTML from component
function generateExampleHTML(tagName: string, props: ComponentProp[]): string {
	const attributes: string[] = [];

	props.forEach((prop) => {
		if (prop.defaultValue !== undefined) {
			if (prop.type === 'json') {
				try {
					const parsed = JSON.parse(prop.defaultValue);
					attributes.push(`${prop.name}='${JSON.stringify(parsed)}'`);
				} catch {
					attributes.push(`${prop.name}="${prop.defaultValue}"`);
				}
			} else if (prop.type === 'boolean') {
				if (prop.defaultValue === 'true') {
					attributes.push(prop.name);
				}
			} else {
				attributes.push(`${prop.name}="${prop.defaultValue}"`);
			}
		} else if (!prop.required) {
			// Add example value for non-required props
			if (prop.type === 'string') {
				attributes.push(`${prop.name}="Example"`);
			} else if (prop.type === 'number') {
				attributes.push(`${prop.name}="0"`);
			}
		}
	});

	const attrsString = attributes.length > 0 ? ' ' + attributes.join(' ') : '';
	return `<${tagName}${attrsString}></${tagName}>`;
}

export function parseComponents(jsString: string): DesignSystemComponent[] {
	const components: DesignSystemComponent[] = [];
	const lines = jsString.split('\n');

	// Parse using AST
	const parsed = parseComponentAST(jsString);

	// Process each component block
	for (const block of parsed.blocks) {
		const tagName = block.tagName;
		const classStartIndex = block.classStartIndex;
		const classStartLine = jsString.substring(0, classStartIndex).split('\n').length - 1;

		const classCode = extractClassCode(block, jsString);

		const props = extractProps(classCode);
		const description = extractDescription(lines, classStartLine);

		const category = inferCategory(tagName, description);
		const exampleHTML = generateExampleHTML(tagName, props);

		const component: DesignSystemComponent = {
			id: tagName,
			name: generateDisplayName(tagName),
			tagName,
			category,
			description,
			props,
			exampleHTML,
		};

		components.push(component);
	}

	return components;
}
